import { CashSession } from '@/@types/cash-management';
import { TDocumentType } from '@/@types/document-type';
import { MoneyInput, Modal, parseMoneyInputValue } from '@/components/common';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Autocomplete,
  Box,
  Button,
  DialogActions,
  DialogContent,
  FormControl,
  IconButton,
  LinearProgress,
  Stack,
  TextField,
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import * as zod from 'zod';
import { useEffect } from 'react';
import { ModalTitle } from '../common/Modal/common/ModalTitle';

const declarationsSchema = zod.object({
  declarations: zod.array(
    zod.object({
      documentTypeId: zod.string().min(1, 'Informe o tipo de documento'),
      declaredAmount: zod
        .number({
          required_error: 'Informe o valor declarado',
          invalid_type_error: 'Informe um valor declarado válido',
        })
        .min(0, 'Informe um valor maior ou igual a zero'),
    })
  ),
});

export type ReconciliationDeclarationsFormData = zod.infer<
  typeof declarationsSchema
>;

type ReconciliationDeclarationsModalProps = {
  isOpen: boolean;
  isLoading: boolean;
  cashSession?: CashSession;
  documentTypes: TDocumentType[];
  onClose: () => void;
  // eslint-disable-next-line no-unused-vars
  onSubmit: (data: ReconciliationDeclarationsFormData) => void;
};

export function ReconciliationDeclarationsModal({
  isOpen,
  isLoading,
  cashSession,
  documentTypes,
  onClose,
  onSubmit,
}: ReconciliationDeclarationsModalProps) {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ReconciliationDeclarationsFormData>({
    resolver: zodResolver(declarationsSchema),
    defaultValues: { declarations: [] },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'declarations',
  });

  useEffect(() => {
    if (cashSession) {
      reset({
        declarations: (cashSession.declarations || []).map((declaration) => ({
          documentTypeId: declaration.documentTypeId,
          declaredAmount: declaration.declaredAmount,
        })),
      });
    }
  }, [cashSession, reset]);

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      closeOnBackdropClick={false}
    >
      {isLoading ? <LinearProgress /> : null}
      <ModalTitle title="Conferência de Caixa" onClose={onClose} />
      <DialogContent dividers>
        <Box
          component="form"
          id="reconciliation-declarations-form"
          onSubmit={handleSubmit(onSubmit)}
        >
          <Stack spacing={2}>
            {fields.map((field, index) => (
              <Stack
                key={field.id}
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                alignItems="flex-start"
              >
                <FormControl fullWidth>
                  <Controller
                    name={`declarations.${index}.documentTypeId`}
                    control={control}
                    render={({
                      field: { value, onChange, ...controllerField },
                    }) => (
                      <Autocomplete
                        options={documentTypes}
                        getOptionLabel={(option) => option.name}
                        noOptionsText="Nenhum registro encontrado"
                        autoHighlight
                        value={
                          value
                            ? documentTypes.find(
                                (documentType) => documentType.id === value
                              ) || null
                            : null
                        }
                        isOptionEqualToValue={(option, selectedOption) =>
                          option.id === selectedOption.id
                        }
                        onChange={(_, newValue) =>
                          onChange(newValue ? newValue.id : '')
                        }
                        renderInput={(params) => (
                          <TextField
                            {...controllerField}
                            {...params}
                            label="Tipo de documento"
                            error={
                              !!errors.declarations?.[index]?.documentTypeId
                            }
                            helperText={
                              errors.declarations?.[index]?.documentTypeId
                                ?.message
                            }
                          />
                        )}
                      />
                    )}
                  />
                </FormControl>
                <Controller
                  name={`declarations.${index}.declaredAmount`}
                  control={control}
                  render={({ field: amountField }) => (
                    <MoneyInput
                      {...amountField}
                      fullWidth
                      label="Valor declarado"
                      error={!!errors.declarations?.[index]?.declaredAmount}
                      helperText={
                        errors.declarations?.[index]?.declaredAmount?.message
                      }
                      onChange={({ target }) =>
                        amountField.onChange(parseMoneyInputValue(target.value))
                      }
                    />
                  )}
                />
                <IconButton color="error" onClick={() => remove(index)}>
                  <Delete />
                </IconButton>
              </Stack>
            ))}
            <Box>
              <Button
                startIcon={<Add />}
                onClick={() =>
                  append({ documentTypeId: '', declaredAmount: 0 })
                }
              >
                Tipo de documento
              </Button>
            </Box>
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3 }}>
        <Button disabled={isLoading} onClick={onClose}>
          Cancelar
        </Button>
        <Button
          type="submit"
          form="reconciliation-declarations-form"
          disabled={isLoading}
          variant="contained"
        >
          Salvar
        </Button>
      </DialogActions>
    </Modal>
  );
}
